import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { PatchException, PatchExceptionKind } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime, formatDay } from '../common'
import { DataTable, type Column } from '../DataTable'
import { chipClass, useIsTenantAdmin } from './labels'

export function tipoEccezioneLabel(k: string): string {
  switch (k) {
    case 'risk_accepted': return t('rischio accettato')
    case 'compensating_control': return t('controllo compensativo')
    case 'false_positive': return t('falso positivo')
    case 'not_fixable': return t('non correggibile')
    default: return k
  }
}

function statoLabel(e: PatchException): string {
  switch (e.status) {
    case 'requested': return t('da approvare')
    case 'approved': return e.days_left <= 14 ? t('attiva, scade fra {n} giorni', { n: e.days_left }) : t('attiva')
    case 'rejected': return t('rifiutata')
    case 'revoked': return t('revocata')
    default: return t('scaduta')
  }
}

type Vista = 'requested' | 'approved' | 'all'

/**
 * Exceptions on missing patches (patch_exception_service): requested by one
 * person, approved or rejected by an administrator (another person, unless
 * self-approval is on), expiring, renewable, revocable.
 */
export function Exceptions() {
  const [vista, setVista] = useState<Vista>('requested')
  const state = useAsync(() => api.patchExceptions(vista === 'all' ? undefined : vista), [vista])
  const admin = useIsTenantAdmin()
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [fatto, setFatto] = useState<string | null>(null)
  const [decisione, setDecisione] = useState<{ e: PatchException; approva: boolean } | null>(null)
  const [nota, setNota] = useState('')

  const esegui = async (azione: () => Promise<unknown>, messaggio: string) => {
    setLavoro(true)
    setErrore(null)
    setFatto(null)
    try {
      await azione()
      setFatto(messaggio)
      setDecisione(null)
      setNota('')
      state.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  const revoca = (e: PatchException) => {
    if (!window.confirm(t('Revocare l’eccezione su {p}? La patch torna subito fra quelle da installare.', { p: e.patch_name || e.patch_key }))) return
    void esegui(() => api.patchExceptionRevoke(e.id), t('Eccezione revocata.'))
  }
  const rinnova = (e: PatchException) => {
    const giorni = 90
    if (!window.confirm(t('Chiedere il rinnovo per {n} giorni dell’eccezione su {p}? Un amministratore dovrà approvarlo.', { n: giorni, p: e.patch_name || e.patch_key }))) return
    void esegui(() => api.patchExceptionRenew(e.id, giorni, ''), t('Rinnovo richiesto.'))
  }

  const colonne: Column<PatchException>[] = [
    { key: 'patch', header: t('Patch'), value: (e) => e.patch_name || e.patch_key, render: (e) => <span className="mono">{e.patch_name || e.patch_key}</span> },
    { key: 'machines', header: t('Macchine'), value: (e) => (e.all_machines ? t('tutte') : e.machines.map((m) => m.name).join(', ')),
      render: (e) => (e.all_machines ? t('tutte') : <>{e.machines.slice(0, 3).map((m, i) => <span key={m.ci_id}>{i > 0 && ', '}<Link to={`/cis/${m.ci_id}`}>{m.name || m.ci_id.slice(0, 8)}</Link></span>)}{e.machines.length > 3 && ` +${e.machines.length - 3}`}</>) },
    { key: 'type', header: t('Tipo'), value: (e) => tipoEccezioneLabel(e.reason_type), filter: 'select' },
    { key: 'reason', header: t('Motivo'), value: (e) => e.reason, render: (e) => <span title={e.reason}>{e.reason.length > 90 ? `${e.reason.slice(0, 90)}…` : e.reason}</span> },
    { key: 'status', header: t('Stato'), value: (e) => statoLabel(e), filter: 'select',
      render: (e) => <span className={chipClass(e.status === 'requested' ? 'warn' : e.in_force ? (e.days_left <= 14 ? 'warn' : 'ok') : 'muted')}>{statoLabel(e)}</span> },
    { key: 'valid_until', header: t('Scade il'), type: 'date', value: (e) => e.valid_until, render: (e) => formatDay(e.valid_until) },
    { key: 'who', header: t('Chi'), value: (e) => `${e.requested_by} ${e.decided_by ?? ''}`,
      render: (e) => <span title={e.decision_note ?? ''}>{e.requested_by}{e.decided_by && <> → {e.decided_by}</>}<div className="text-muted patch-sub">{formatDateTime(e.requested_at)}</div></span> },
    {
      key: 'actions', header: '', value: () => '', sort: false, filter: false,
      render: (e) => (
        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {admin && e.status === 'requested' && <>
            <button type="button" className="link" disabled={lavoro} onClick={() => setDecisione({ e, approva: true })}>{t('Approva')}</button>
            <button type="button" className="link" disabled={lavoro} onClick={() => setDecisione({ e, approva: false })}>{t('Rifiuta')}</button>
          </>}
          {(e.status === 'approved' || e.status === 'expired') && <button type="button" className="link" disabled={lavoro} onClick={() => rinnova(e)}>{t('Rinnova')}</button>}
          {admin && e.status === 'approved' && e.in_force && <button type="button" className="link" disabled={lavoro} onClick={() => revoca(e)}>{t('Revoca')}</button>}
        </span>
      ),
    },
  ]

  return (
    <Card title={t('Eccezioni')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Una patch che non si installa per una ragione — rischio accettato, controllo compensativo, falso positivo, non correggibile — su tutte le macchine o su alcune, fino a una scadenza. La chiede chiunque possa scrivere nel Patching (dal catalogo, «Chiedi un’eccezione»), la approva o la rifiuta un amministratore diverso da chi l’ha chiesta. Finché vale, la patch su quelle macchine non conta negli SLA, nella conformità, nelle priorità e nelle politiche; 14 giorni prima della scadenza parte una mail a chi l’ha chiesta e approvata.')}
      </p>
      <div className="patch-toolbar">
        <div className="segmented" role="group" aria-label={t('Quali')}>
          <button type="button" className={vista === 'requested' ? 'segmented__on' : ''} onClick={() => setVista('requested')}>{t('Da approvare')}</button>
          <button type="button" className={vista === 'approved' ? 'segmented__on' : ''} onClick={() => setVista('approved')}>{t('Attive')}</button>
          <button type="button" className={vista === 'all' ? 'segmented__on' : ''} onClick={() => setVista('all')}>{t('Tutte')}</button>
        </div>
        {admin && state.data && (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={state.data.settings.self_approval} disabled={lavoro}
                   onChange={(ev) => void esegui(() => api.patchExceptionSettings(ev.target.checked), t('Impostazione salvata.'))} />
            {t('Chi chiede può anche approvare (senza quattro occhi)')}
          </label>
        )}
      </div>
      <ErrorBanner error={errore ?? state.error} />
      {fatto && <div className="banner banner--ok">{fatto}</div>}
      {decisione && (
        <form className="form-dialog" onSubmit={(ev) => {
          ev.preventDefault()
          const { e, approva } = decisione
          if (!window.confirm(approva
            ? t('Confermi l’approvazione? Fino al {d} la patch non conterà su quelle macchine.', { d: formatDay(e.valid_until) })
            : t('Confermi il rifiuto?'))) return
          void esegui(() => api.patchExceptionDecide(e.id, approva, nota), approva ? t('Eccezione approvata.') : t('Eccezione rifiutata.'))
        }}>
          <h4 className="form__sezione">{decisione.approva ? t('Approva l’eccezione su {p}', { p: decisione.e.patch_name || decisione.e.patch_key }) : t('Rifiuta l’eccezione su {p}', { p: decisione.e.patch_name || decisione.e.patch_key })}</h4>
          <p className="field__hint" style={{ marginTop: 0 }}>{tipoEccezioneLabel(decisione.e.reason_type)} · {decisione.e.reason}</p>
          <label className="field"><span className="field__label">{decisione.approva ? t('Nota (facoltativa)') : t('Perché la rifiuti')}</span>
            <textarea value={nota} onChange={(ev) => setNota(ev.target.value)} rows={3} maxLength={2000} required={!decisione.approva} /></label>
          <div className="form-dialog__azioni">
            <button type="button" className="secondary" onClick={() => setDecisione(null)}>{t('Annulla')}</button>
            <button type="submit" disabled={lavoro}>{decisione.approva ? t('Approva') : t('Rifiuta')}</button>
          </div>
        </form>
      )}
      {!state.data ? !state.error && <Spinner /> : state.data.exceptions.length === 0
        ? <EmptyState title={vista === 'requested' ? t('Nessuna richiesta da decidere.') : t('Nessuna eccezione.')} />
        : <DataTable rows={state.data.exceptions} columns={colonne} rowKey={(e) => e.id} conteggio={{ nome: t('eccezioni') }} />}
    </Card>
  )
}

/** The request form, from a patch of the catalogue. */
export function RichiestaEccezione({ patchKey, patchName, hosts, onClose, onDone }: {
  patchKey: string
  patchName: string
  hosts: { ci_id: string; name: string }[]
  onClose: () => void
  onDone: () => void
}) {
  const [tipo, setTipo] = useState<PatchExceptionKind>('risk_accepted')
  const [motivo, setMotivo] = useState('')
  const [giorni, setGiorni] = useState(90)
  const [tutte, setTutte] = useState(true)
  const [scelte, setScelte] = useState<Set<string>>(new Set(hosts.map((h) => h.ci_id)))
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const invia = async () => {
    setLavoro(true)
    setErrore(null)
    try {
      await api.patchExceptionRequest({ patch_key: patchKey, patch_name: patchName, ci_ids: tutte ? [] : [...scelte],
                                        reason_type: tipo, reason: motivo, days: giorni })
      onDone()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Chiedi un’eccezione')}>
      <div className="dob-modal__scrim" onClick={onClose} />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <h3 className="dob-modal__titolo">{t('Eccezione su {p}', { p: patchName || patchKey })}</h3>
          <button type="button" className="secondary" onClick={onClose}>{t('Chiudi')}</button>
        </div>
        <ErrorBanner error={errore} />
        <form className="form-dialog" onSubmit={(e) => { e.preventDefault(); void invia() }}>
          <div className="form-grid">
            <label className="field span-2"><span className="field__label">{t('Tipo')}</span>
              <select value={tipo} onChange={(e) => setTipo(e.target.value as PatchExceptionKind)}>
                {(['risk_accepted', 'compensating_control', 'false_positive', 'not_fixable'] as const).map((k) => <option key={k} value={k}>{tipoEccezioneLabel(k)}</option>)}
              </select></label>
            <label className="field span-2"><span className="field__label">{t('Durata (giorni, al più 365)')}</span>
              <input type="number" min={1} max={365} value={giorni} onChange={(e) => setGiorni(Number(e.target.value))} required /></label>
            <label className="field span-4"><span className="field__label">{t('Motivo')}</span>
              <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} minLength={10} maxLength={4000} required
                        placeholder={t('es. il fornitore del PACS non certifica ancora la versione nuova; la porta è chiusa dal firewall')} /></label>
            <div className="field span-4">
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={tutte} onChange={(e) => setTutte(e.target.checked)} />
                {t('Su tutte le macchine a cui manca (anche quelle che si aggiungeranno)')}
              </label>
              {!tutte && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
                  {hosts.map((h) => (
                    <label key={h.ci_id} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <input type="checkbox" checked={scelte.has(h.ci_id)}
                             onChange={(e) => { const n = new Set(scelte); if (e.target.checked) n.add(h.ci_id); else n.delete(h.ci_id); setScelte(n) }} />
                      {h.name || h.ci_id.slice(0, 8)}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="field__hint">{t('La richiesta va approvata da un amministratore diverso da te; finché non è approvata la patch conta come prima.')}</p>
          <div className="form-dialog__azioni">
            <button type="submit" disabled={lavoro || motivo.trim().length < 10 || (!tutte && scelte.size === 0)}>{lavoro ? t('Invio…') : t('Chiedi l’eccezione')}</button>
          </div>
        </form>
      </div>
    </div>
  )
}
